import { z } from 'zod';
import type { HackathonProject, Track } from './projects';
import {
  PROJECT_SUBMISSION_END_DATE_BUFFER,
  submitProjectMediaSchema,
} from './projects';

export const HackathonPhase = {
  REGISTRATION: 1,
  PROJECT_CREATION: 2,
  PROJECT_SUBMISSION: 3,
  HACKATHON_ENDED: 4,
  WINNERS_ANNOUNCED: 5,
} as const;

export type HackathonPhase =
  (typeof HackathonPhase)[keyof typeof HackathonPhase];

export const HackathonParticipationStatus = {
  NOT_REGISTERED: 1,
  REGISTERED: 2,
  PROJECT_CREATED: 3,
  PROJECT_SUBMITTED: 4,
} as const;

export type HackathonParticipationStatus =
  (typeof HackathonParticipationStatus)[keyof typeof HackathonParticipationStatus];

export function getHackathonPhaseLabel(phase: HackathonPhase): string {
  switch (phase) {
    case HackathonPhase.REGISTRATION:
      return 'Registration';
    case HackathonPhase.PROJECT_CREATION:
      return 'Project Creation';
    case HackathonPhase.PROJECT_SUBMISSION:
      return 'Project Submission';
    case HackathonPhase.HACKATHON_ENDED:
      return 'Hackathon Ended';
    case HackathonPhase.WINNERS_ANNOUNCED:
      return 'Winners Announced';
  }
}

export function getParticipationStatusLabel(
  status: HackathonParticipationStatus,
): string {
  switch (status) {
    case HackathonParticipationStatus.NOT_REGISTERED:
      return 'Not Registered';
    case HackathonParticipationStatus.REGISTERED:
      return 'Registered';
    case HackathonParticipationStatus.PROJECT_CREATED:
      return 'Project Created';
    case HackathonParticipationStatus.PROJECT_SUBMITTED:
      return 'Project Submitted';
  }
}

export interface Hackathon {
  id: number;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date | null;
  projectSubmissionStartDate: Date;
  projectSubmissionEndDate: Date;
  isProjectDirectoryEnabled: boolean;
  largeBannerImageUrl: string;
  smallBannerImageUrl: string;
  landingPageUrl: string;
}

export interface HackathonWithPhase extends Hackathon {
  phase: HackathonPhase;
  currentWeekNumber: number | null;
  weeklySubmissionStart: Date | null;
  weeklySubmissionEnd: Date | null;
  isCurrentWeekUpdateSubmitted: boolean;
}

export interface HackathonWithState extends HackathonWithPhase {
  participationStatus: HackathonParticipationStatus;
}

export const RADAR_TRACKS = [
  'DePIN',
  'DeFi',
  'DAOs & Network States',
  'Payments',
  'Consumer',
  'Gaming',
  'Infrastructure',
];

export const BREAKOUT_TRACKS = [
  'DePIN',
  'DeFi',
  'Gaming',
  'Infrastructure',
  'Consumer Apps',
  'Stablecoins',
  'AI',
] as const;

export const HackathonPrizeType = {
  GrandPrize: 'GRAND_PRIZE',
  TrackPrize: 'TRACK_PRIZE',
  ClimateAward: 'CLIMATE_AWARD',
  PublicGoodsAward: 'PUBLIC_GOODS_AWARD',
  UniversityAward: 'UNIVERSITY_AWARD',
  HonorableMention: 'HONORABLE_MENTION',
} as const;

export const HackathonPrizeGroup = {
  GrandPrize: 'GRAND_PRIZE',
  TrackPrize: 'TRACK_PRIZE',
  Award: 'AWARD',
  HonorableMention: 'HONORABLE_MENTION',
} as const;

export type HackathonPrizeGroup =
  (typeof HackathonPrizeGroup)[keyof typeof HackathonPrizeGroup];

export type HackathonPrizeType =
  (typeof HackathonPrizeType)[keyof typeof HackathonPrizeType];

export const HackathonPrizeTypeToGroupMap = new Map<
  HackathonPrizeType,
  HackathonPrizeGroup
>([
  [HackathonPrizeType.GrandPrize, HackathonPrizeGroup.GrandPrize],
  [HackathonPrizeType.TrackPrize, HackathonPrizeGroup.TrackPrize],
  [HackathonPrizeType.ClimateAward, HackathonPrizeGroup.Award],
  [HackathonPrizeType.PublicGoodsAward, HackathonPrizeGroup.Award],
  [HackathonPrizeType.UniversityAward, HackathonPrizeGroup.Award],
  [HackathonPrizeType.HonorableMention, HackathonPrizeGroup.HonorableMention],
]);

type HackathonPrizeTrack = {
  name: string;
  type: Track;
};

export type HackathonPrize = {
  type: HackathonPrizeType;
  group: HackathonPrizeGroup;
  track: HackathonPrizeTrack | null;
  name: string;
  prizeAmount: number;
  placement: 1 | 2 | 3 | 4 | 5 | null;
};

export type HackathonWinnerGroup = {
  id: HackathonPrizeGroup;
  name: string;
  winners: HackathonProject[];
};

export interface HackathonWeeklyUpdate {
  weekNumber: number;
  submittedAt: Date;
}

/**
 * Active here defined as users can still register, submit projects, etc.
 * @param hackathon - The hackathon to check
 * @returns True if the hackathon is active, false otherwise
 */
export function isHackathonActive(hackathon: HackathonWithState) {
  const phase = getHackathonPhase(hackathon);
  return phase < HackathonPhase.HACKATHON_ENDED;
}

/**
 * Get the phase of a hackathon
 * @param hackathon - The hackathon to get the phase for
 * @returns The phase of the hackathon
 */

export function getHackathonPhase(
  hackathon: Pick<
    Hackathon,
    | 'startDate'
    | 'projectSubmissionStartDate'
    | 'projectSubmissionEndDate'
    | 'endDate'
  >,
) {
  const currentDate = new Date();
  const projectSubmissionEndDatePlusBuffer = new Date(
    new Date(hackathon.projectSubmissionEndDate).getTime() +
      PROJECT_SUBMISSION_END_DATE_BUFFER,
  );

  let phase: HackathonPhase;

  if (currentDate < hackathon.startDate) {
    phase = HackathonPhase.REGISTRATION;
  } else if (currentDate < hackathon.projectSubmissionStartDate) {
    phase = HackathonPhase.PROJECT_CREATION;
  } else if (
    currentDate >= hackathon.projectSubmissionStartDate &&
    currentDate < projectSubmissionEndDatePlusBuffer
  ) {
    phase = HackathonPhase.PROJECT_SUBMISSION;
  } else if (!hackathon.endDate || currentDate < hackathon.endDate) {
    phase = HackathonPhase.HACKATHON_ENDED;
  } else {
    phase = HackathonPhase.WINNERS_ANNOUNCED;
  }

  return phase;
}

export const hackathonTeamMemberSchema = z.object({
  id: z.number().min(1, { message: 'Team member ID is required.' }),
  isEditor: z.boolean().default(true),
});

export const hackathonProjectAndTeamSchema = z.object({
  name: z.string().min(1, { message: 'Name is required.' }),
  description: z.string().min(1, { message: 'Description is required.' }),
  tracks: z
    .array(z.string())
    .max(3, { message: 'You may only select up to 3 tracks.' })
    .optional(),
  country: z.string().min(1, { message: 'Country is required.' }),
  repoLink: z.string().nullable(),
  teamMembers: z
    .array(hackathonTeamMemberSchema)
    .min(1, { message: 'At least one team member is required.' }),
});

export const githubRepoRegex =
  /^https:\/\/github\.com\/[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/;

export const submitHackathonProjectAndTeamSchema =
  hackathonProjectAndTeamSchema.extend({
    repoLink: z
      .string()
      .trim()
      .regex(githubRepoRegex, { message: 'Invalid GitHub repository link.' })
      .min(1, { message: 'Repository link is required.' }),
    tracks: z
      .array(z.string())
      .min(1, { message: 'At least one track is required.' })
      .max(3, { message: 'You may only select up to 3 tracks.' }),
  });

export const validateHackathonProjectSubmissionSchema =
  submitHackathonProjectAndTeamSchema.merge(submitProjectMediaSchema);

/**
 * Calculates the current week number of a hackathon (1-based).
 * @param hackathonStartDate The start date of the hackathon.
 * @param currentDate The current date.
 * @returns The week number (1-based) or null if the hackathon hasn't started.
 */
export function calculateHackathonWeek(
  hackathonStartDate: Date,
  currentDate: Date,
): number | null {
  if (currentDate < hackathonStartDate) {
    return null;
  }

  const diffMillis = currentDate.getTime() - hackathonStartDate.getTime();
  // Add a small buffer to handle potential floating point inaccuracies at the week boundary
  const diffDays = diffMillis / (1000 * 60 * 60 * 24) + 0.00001;
  const weekNumber = Math.floor(diffDays / 7) + 1;

  return weekNumber;
}

/**
 * Calculates the end date/time for a specific hackathon week.
 * Each week ends exactly 7 days after the previous week ended (or the hackathon started for week 1).
 * The end date represents the *exclusive* end (i.e., the very start of the next period).
 * This is using setDate instead of some fixed millisecond addition to handle
 * things like daylight savings time, leap years, etc.
 * @param hackathonStartDate The start date of the hackathon.
 * @param weekNumber The target week number (1-based).
 * @returns The Date object representing the end of the week, or null if weekNumber < 1.
 */
export function getWeeklyUpdateSubmissionEndDate(
  hackathonStartDate: Date,
  weekNumber: number,
): Date | null {
  if (weekNumber < 1) {
    return null;
  }
  // Calculate the start date of the target week
  const weekStartDate = new Date(hackathonStartDate);
  weekStartDate.setDate(weekStartDate.getDate() + (weekNumber - 1) * 7);

  // Calculate the end of the week (7 days after week start)
  const weekEndDate = new Date(weekStartDate);
  weekEndDate.setDate(weekEndDate.getDate() + 7);

  return weekEndDate;
}

/**
 * Calculates the start date/time for the weekly update submission window.
 * The window opens 72 hours (3 days) before the end of the week.
 * @param hackathonStartDate The start date of the hackathon.
 * @param weekNumber The target week number (1-based).
 * @returns The Date object representing the start of the submission window, or null if weekNumber < 1.
 */
export function getWeeklyUpdateSubmissionStartDate(
  hackathonStartDate: Date,
  weekNumber: number,
): Date | null {
  const weekEndDate = getWeeklyUpdateSubmissionEndDate(
    hackathonStartDate,
    weekNumber,
  );

  if (!weekEndDate) {
    return null;
  }

  const submissionStartDate = new Date(weekEndDate);
  submissionStartDate.setDate(submissionStartDate.getDate() - 3); // Subtract 3 days

  return submissionStartDate;
}
